/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { getAuthToken } from '@services/auth/auth';
import {
  TelemetryService,
  type TelemetryEvent,
} from './telemetry-service';

/**
 * Authenticated-only client telemetry emitter.
 *
 * Buffers client-origin usage events (page views, form timing, anti-bot/auth
 * with-vs-without-refresh outcomes) and flushes them in batches to the
 * authenticated `POST /telemetry/events` collector. It fires ONLY when a user
 * is authenticated (a stored OIDC access token exists) -- there is no public
 * or anonymous ingest, and no `sendBeacon` fallback.
 *
 * Flushing is triggered on a timer, when the buffer reaches a cap, and when the
 * tab is hidden (so a navigation-away does not lose the tail of the buffer).
 */

/** Flush when this many events accumulate. */
const MAX_BUFFER = 25;
/** Flush at most this many events in one request (matches the server cap). */
const MAX_BATCH = 100;
/** Periodic flush interval, milliseconds. */
const FLUSH_INTERVAL_MS = 15_000;

let buffer: TelemetryEvent[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let listenersInstalled = false;
let sessionId: string | null = null;

/**
 * Whether telemetry may be emitted right now: a browser context exists AND the
 * user is authenticated. Never throws.
 */
function canEmit(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(getAuthToken());
  } catch {
    return false;
  }
}

/**
 * Lazily derive a per-tab session id so events from the same browsing session
 * can be grouped. Best-effort; falls back to a volatile id when storage is
 * unavailable.
 */
function getSessionId(): string {
  if (sessionId) return sessionId;
  const generated = `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  try {
    const existing = window.sessionStorage.getItem('housien.telemetry.session');
    if (existing) {
      sessionId = existing;
      return sessionId;
    }
    window.sessionStorage.setItem('housien.telemetry.session', generated);
  } catch {
    // sessionStorage may be unavailable (private mode / SSR) -- use volatile.
  }
  sessionId = generated;
  return sessionId;
}

/** The app version, when the build injected one. */
function appVersion(): string | undefined {
  try {
    return (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_APP_VERSION;
  } catch {
    return undefined;
  }
}

/**
 * Install the periodic timer and visibility/pagehide flush listeners exactly
 * once. Called lazily on the first emit so no work happens before telemetry is
 * actually used.
 */
function ensureListeners(): void {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;

  timer = setInterval(() => {
    void flush();
  }, FLUSH_INTERVAL_MS);

  const flushNow = () => {
    void flush();
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushNow();
  });
  window.addEventListener('pagehide', flushNow);
}

/**
 * Enqueue one client telemetry event. No-ops when the user is not
 * authenticated (authenticated-only ingest). Session id and app version are
 * stamped automatically; an over-cap buffer triggers an immediate flush.
 *
 * @param event - The client event to record (identity is stamped server-side).
 */
export function emitTelemetry(event: TelemetryEvent): void {
  if (!canEmit()) return;
  ensureListeners();
  buffer.push({
    ...event,
    session_id: event.session_id ?? getSessionId(),
    app_version: event.app_version ?? appVersion(),
  });
  if (buffer.length >= MAX_BUFFER) {
    void flush();
  }
}

/**
 * Flush the buffered events to the collector. No-ops when the buffer is empty
 * or the user is no longer authenticated. Any transport error is swallowed and
 * the batch is dropped (telemetry must never surface an error to the user).
 *
 * @returns A promise that resolves once the in-flight batch settles.
 */
export async function flush(): Promise<void> {
  if (buffer.length === 0 || !canEmit()) return;
  const batch = buffer.slice(0, MAX_BATCH);
  buffer = buffer.slice(batch.length);
  try {
    await TelemetryService.ingestEvents(batch);
  } catch {
    // Drop on failure -- telemetry is best-effort and never blocks the user.
  }
}

/**
 * Reset emitter state. Test-only hook so suites start from a clean buffer and
 * listener state.
 */
export function __resetTelemetryForTest(): void {
  buffer = [];
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  listenersInstalled = false;
  sessionId = null;
}
